import React from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, Divider, Link as MuiLink, Grid, Stack } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faDiscord, faFacebook, faTiktok, faSnapchat } from '@fortawesome/free-brands-svg-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useUserData } from '../auth/UserData'; // Importer autentiseringskontekst
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';

const Footer = () => {
  const { t } = useTranslation();
  const { darkMode, tenantConfig } = useTheme();
  const { user } = useUserData();

  const isWhitelabel = tenantConfig && tenantConfig.slug && tenantConfig.slug !== 'flocktrade';
  const socialLinks = tenantConfig?.settings?.socialLinks || {};
  const hidePoweredBy = tenantConfig?.settings?.hidePoweredBy === true;

  const trackClick = async (link) => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/stats/track-click`, {
        link,
      });
    } catch (error) {
      console.error('Error tracking click:', error.message);
    }
  };

  return (
    <AppBar
      position="static"
      color="primary"
      component="footer"
      role="contentinfo"
      sx={{
        marginTop: 4,
        top: 'auto',
        bottom: 0,
        backgroundColor: darkMode ? '#0A1929' : '#007FFF', // Dynamisk bakgrunnsfarge
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        {/* Footer Text */}
        <Typography variant="body2" align="center" sx={{ mb: { xs: 1, md: 0 } }}>
          {isWhitelabel
            ? `© ${new Date().getFullYear()} ${tenantConfig.name}. All Rights Reserved.`
            : t('footer.copyright', { year: new Date().getFullYear() })}
        </Typography>

        {/* Social Media Links */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {(socialLinks.instagram || !isWhitelabel) && (
            <IconButton
              aria-label="Instagram"
              color="inherit"
              onClick={() => { trackClick('Sosial: Instagram'); window.open(socialLinks.instagram || 'https://www.instagram.com/flocktrade/', '_blank')}}
            >
              <FontAwesomeIcon icon={faInstagram} size="lg" />
            </IconButton>
          )}

          {(socialLinks.discord || !isWhitelabel) && (
            <IconButton
              aria-label="Discord"
              color="inherit"
              onClick={() => { trackClick('Sosial: Discord'); window.open(socialLinks.discord || 'https://discord.com/invite/sQjPufbufK', '_blank')}}
            >
              <FontAwesomeIcon icon={faDiscord} size="lg" />
            </IconButton>
          )}

          {(socialLinks.tiktok || !isWhitelabel) && (
            <IconButton
              aria-label="TikTok"
              color="inherit"
              onClick={() => {trackClick('Sosial: TikTok'); window.open(socialLinks.tiktok || 'https://tiktok.com/@flocktrade', '_blank')}}
            >
              <FontAwesomeIcon icon={faTiktok} size="lg" />
            </IconButton>
          )}

          {(socialLinks.facebook || !isWhitelabel) && (
            <IconButton
              aria-label="Facebook"
              color="inherit"
              onClick={() => { trackClick('Sosial: Facebook'); window.open(socialLinks.facebook || 'https://facebook.com/flocktradecom', '_blank')}}
            >
              <FontAwesomeIcon icon={faFacebook} size="lg" />
            </IconButton>
          )}

          {(socialLinks.snapchat || !isWhitelabel) && (
            <IconButton
              aria-label="Snapchat"
              color="inherit"
              onClick={() => {trackClick('Sosial: Snapchat');window.open(socialLinks.snapchat || 'https://www.snapchat.com/add/FlockTrade.com', '_blank')}}
            >
              <FontAwesomeIcon icon={faSnapchat} size="lg" />
            </IconButton>
          )}
        </Box>
      </Toolbar>

            {/* Divider */}
            <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />

            {/* Footer Links Grid */}
            {!isWhitelabel && (
              <Box sx={{ px: { xs: 2, md: 6 }, py: 3 }}>
                <Grid container spacing={4} justifyContent="center">
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1.5 }}>
                      Features
                    </Typography>
                    <Stack spacing={0.8}>
                      <MuiLink component={RouterLink} to="/signals" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>Signals</MuiLink>
                      <MuiLink component={RouterLink} to="/sentiment" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>Sentiment</MuiLink>
                      <MuiLink component={RouterLink} to="/calendar" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>Forex Calendar</MuiLink>
                      <MuiLink component={RouterLink} to="/pricing" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>Pricing</MuiLink>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1.5 }}>
                      Community
                    </Typography>
                    <Stack spacing={0.8}>
                      <MuiLink href="https://discord.com/invite/sQjPufbufK" target="_blank" rel="noopener" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>Discord</MuiLink>
                      <MuiLink href="https://www.instagram.com/flocktrade" target="_blank" rel="noopener" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>Instagram</MuiLink>
                      <MuiLink href="https://www.tiktok.com/@flocktrade" target="_blank" rel="noopener" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>TikTok</MuiLink>
                      <MuiLink href="https://www.facebook.com/flocktradecom" target="_blank" rel="noopener" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>Facebook</MuiLink>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1.5 }}>
                      Company
                    </Typography>
                    <Stack spacing={0.8}>
                      <MuiLink component={RouterLink} to="/privacy" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>{t('footer.privacyPolicy')}</MuiLink>
                      <MuiLink component={RouterLink} to="/whitelabel" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>Whitelabel</MuiLink>
                      <MuiLink component={RouterLink} to="/docs" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>Documentation</MuiLink>
                      <MuiLink href="mailto:support@flocktrade.com" underline="hover" color="inherit" sx={{ fontSize: '0.82rem' }}>Contact</MuiLink>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />

            {/* Disclaimer Section */}
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography
                variant="caption"
                color={darkMode ? 'text.secondary' : '#ffffff'}
                >
                {isWhitelabel
                  ? `All content provided on this platform is for informational purposes only and does not constitute financial advice. ${tenantConfig.name} does not guarantee trading results, and all trading involves significant risk. Please consult a financial advisor before making any investment decisions.`
                  : t('footer.disclamer')}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                {isWhitelabel && (
                  <MuiLink
                    component={RouterLink}
                    to="/privacy"
                    underline="hover"
                    color="inherit"
                    onClick={() => trackClick('Footer: Privacy policy')}
                    sx={{ fontSize: '0.8rem', fontWeight: 600 }}
                  >
                    {t('footer.privacyPolicy')}
                  </MuiLink>
                )}
              </Box>
              {isWhitelabel && !hidePoweredBy && (
                <Box sx={{ mt: 1.5, opacity: 0.7 }}>
                  <Typography variant="caption" color="inherit" sx={{ fontSize: '0.7rem' }}>
                    {t('footer.poweredBy')}{' '}
                    <MuiLink
                      href="https://flocktrade.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="inherit"
                      underline="hover"
                      sx={{ fontWeight: 600 }}
                    >
                      FlockTrade
                    </MuiLink>
                  </Typography>
                </Box>
              )}
            </Box>

    </AppBar>
  );
};

export default Footer;
